import * as FilePond from "filepond";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';

FilePond.registerPlugin(FilePondPluginImagePreview, FilePondPluginFileValidateSize, FilePondPluginFileValidateType);

/**
 * Wires a FilePond uploader to a hidden input that receives a JSON array of the
 * temporary media ids returned by /umbraco/api/tributemedia/uploadimage.
 *
 * Any <input type="file" data-filepond data-target="HiddenInputId"> is picked up,
 * plus the legacy #FundImagePond / #FundImageInput pair used on Create/Edit Fund.
 */
function initPond(fileInput, hiddenInput) {
    if (!fileInput || !hiddenInput || fileInput.dataset.filepondInitialised) {
        return;
    }

    fileInput.dataset.filepondInitialised = "true";

    const maxUploads = parseInt(fileInput.dataset.maxUploads, 10) || 1;

    let uploadedIds = [];

    function updateHiddenInput() {
        hiddenInput.value = uploadedIds.length > 0 ? JSON.stringify(uploadedIds) : "";
    }

    const labelIdleText = maxUploads === 1
        ? 'Drag & Drop your image or <span class="filepond--label-action">Browse</span>'
        : 'Drag & Drop your images or <span class="filepond--label-action">Browse</span>';

    const pond = FilePond.create(fileInput, {
        allowMultiple: maxUploads > 1,
        acceptedFileTypes: ["image/png", "image/jpeg"],
        maxFileSize: "5MB",
        maxFiles: maxUploads,
        labelIdle: labelIdleText,
        labelMaxFileSizeExceeded: "File is too large. Maximum size is 5MB",
        labelMaxFileSize: "Maximum file size is 5MB",
        labelMaxFileExceeded: `You can only upload up to ${maxUploads} files.`,
        maxParallelUploads: 1,
        server: {
            process: {
                url: "/umbraco/api/tributemedia/uploadimage",
                method: "POST",
                onload: (response) => {
                    try {
                        const json = JSON.parse(response);
                        const fileArray = Array.isArray(json) ? json : [json];

                        fileArray.forEach(fileData => {
                            if (fileData && fileData.id) {
                                uploadedIds.push(fileData.id);
                            }
                        });

                        uploadedIds = [...new Set(uploadedIds)];
                        updateHiddenInput();

                        return fileArray[0].id;
                    } catch (err) {
                        console.error("Invalid JSON response:", response);
                        return Promise.reject("Invalid server response");
                    }
                },
                onerror: (err) => {
                    console.error("Upload error:", err);
                    return "Upload failed";
                }
            },
            revert: (uniqueFileId, load, error) => {
                uploadedIds = uploadedIds.filter(id => id !== uniqueFileId);
                updateHiddenInput();
                load();
            }
        }
    });

    const removeServerId = (file) => {
        if (file && file.serverId) {
            uploadedIds = uploadedIds.filter(id => id !== file.serverId);
            updateHiddenInput();
        }
    };

    pond.on("removefile", (error, file) => removeServerId(file));
    pond.on("processfileabort", (file) => removeServerId(file));
    pond.on("processfileundo", (file) => removeServerId(file));
}

// Legacy pair used on Create Fund / Edit Fund.
initPond(document.getElementById("FundImagePond"), document.getElementById("FundImageInput"));

// Data-attribute driven uploaders (e.g. the funeral director logo).
document.querySelectorAll("input[type=file][data-filepond]").forEach((fileInput) => {
    const target = fileInput.dataset.target;
    initPond(fileInput, target ? document.getElementById(target) : null);
});
